import React, { useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../../../constants/api';
import { decodeToken, getToken } from '../../../utils/TokenUtils';
import { exportElementToPDF } from '../../../utils/ktcUtils';
import { notification } from 'antd';
import { Link } from 'react-router-dom';

const BalanceSheet = ({ report, link, value }) => {
  const reportRef = useRef(null);
  const token = getToken();
  const companyName = decodeToken(token, 'companyName');
  const companyBanner = decodeToken(token, 'banner');

  /* ---------------- helpers ---------------- */
  const parseAmount = (v) =>
    typeof v === 'string'
      ? parseFloat(v.replace(/[^0-9.-]/g, ''))
      : Number(v || 0);

  const formatAmount = (v) => {
    const n = Number(v || 0);
    const formatted = Math.abs(n).toLocaleString(undefined, {
      minimumFractionDigits: 2,
    });
    return n < 0 ? `(${formatted})` : formatted;
  };

  /* ---------------- BUILD BALANCE SHEET ---------------- */
  const statement = useMemo(() => {
    if (!Array.isArray(report)) return [];
    const buildSection = (accountType) => {
      const subs = {};

      report
        .filter((r) => r.AccountType?.toLowerCase() === accountType)
        .forEach((r) => {
          const subType = r.SubAccountType || 'Other';
          const section = r.Section || 'Other';

          if (!subs[subType]) subs[subType] = {};
          if (!subs[subType][section]) subs[subType][section] = [];

          subs[subType][section].push({
            accountType: r.AccountType,
            subAccountType: r.SubAccountType,
            accountId: r.account_id,
            mainAccountId: r.main_account_id,
            label: r.Account,
            amount: parseAmount(r.Amount),
          });
        });

      return Object.entries(subs).map(([subType, sections]) => {
        const sectionList = Object.entries(sections).map(([section, rows]) => ({
          section,
          rows,
          total: rows.reduce((s, r) => s + r.amount, 0),
        }));

        return {
          subType,
          sections: sectionList,
          total: sectionList.reduce((s, sec) => s + sec.total, 0), // 👈 ADD
        };
      });
    };

    const assets = buildSection('asset');
    const liabilities = buildSection('liability');
    const equity = buildSection('equity');

    const totalAssets = assets.reduce(
      (sum, sub) => sum + sub.sections.reduce((s, sec) => s + sec.total, 0),
      0
    );

    const totalLiabilities = liabilities.reduce(
      (sum, sub) => sum + sub.sections.reduce((s, sec) => s + sec.total, 0),
      0
    );

    const totalEquity = equity.reduce(
      (sum, sub) => sum + sub.sections.reduce((s, sec) => s + sec.total, 0),
      0
    );

    return {
      assets,
      liabilities,
      equity,
      totals: {
        assets: totalAssets,
        liabilities: totalLiabilities,
        equity: totalEquity,
      },
    };
  }, [report]);

  console.log('statement:', statement);
  /* ---------------- EXPORT HELPERS ---------------- */
  const flattenForExport = () => {
    const rows = [];

    const pushGroups = (title, groups, total) => {
      rows.push({ Account: title, Amount: '' });

      groups.forEach((sub) => {
        // SubAccountType Header
        rows.push({ Account: sub.subType, Amount: '' });

        sub.sections.forEach((sec) => {
          // Skip section if same name as subAccountType
          if (sub.subType?.toLowerCase() !== sec.section?.toLowerCase()) {
            rows.push({ Account: '  ' + sec.section, Amount: '' });
          }

          sec.rows.forEach((r) =>
            rows.push({
              Account: '    ' + r.label, // Account indentation
              Amount: r.amount,
            })
          );

          // Section Total
          rows.push({
            Account:
              '  ' +
              (sub.subType?.toLowerCase() === sec.section?.toLowerCase()
                ? `Total ${sub.subType}`
                : `Total ${sec.section}`),
            Amount: sec.total,
          });

          rows.push({ Account: '', Amount: '' });
        });

        // SubAccountType Total (only if multiple sections)
        if (sub.sections.length > 1) {
          rows.push({
            Account: `Total ${sub.subType}`,
            Amount: sub.total,
          });
          rows.push({ Account: '', Amount: '' });
        }
      });

      rows.push({
        Account: `TOTAL ${title.toUpperCase()}`,
        Amount: total,
      });

      rows.push({ Account: '', Amount: '' });
    };

    pushGroups('Assets', statement.assets, statement.totals.assets);
    pushGroups(
      'Liabilities',
      statement.liabilities,
      statement.totals.liabilities
    );
    pushGroups('Equity', statement.equity, statement.totals.equity);

    return rows;
  };

  /* ---------------- EXPORT ACTIONS ---------------- */
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(flattenForExport());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet');
    XLSX.writeFile(wb, `Balance Sheet as of ${value.to}.xlsx`);
  };

  const exportCSV = () => {
    const rows = flattenForExport();
    const csv =
      'Account,Amount\n' +
      rows.map((r) => `"${r.Account}",${r.Amount}`).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Balance Sheet as of ${value.to}.csv`;
    link.click();
  };

  const exportPDF = async () => {
    try {
      await exportElementToPDF({
        element: reportRef.current,
        fileName: `Balance Sheet as of ${value.to}.pdf`,
        orientation: 'p',
      });
    } catch (err) {
      notification.error({
        message: 'PDF Export Failed',
        description: err.message,
      });
    }
  };

  const renderAccountType = (groups) =>
    groups.map((sub, i) => (
      <React.Fragment key={i}>
        {/* SubAccountType Header */}
        <tr>
          <td style={{ paddingLeft: 12, fontWeight: 'bold' }}>{sub.subType}</td>
          <td></td>
        </tr>

        {sub.sections.map((sec, j) => {
          const sameName =
            sub.subType?.toLowerCase() === sec.section?.toLowerCase();

          return (
            <React.Fragment key={j}>
              {/* Section Header (skip if duplicate) */}
              {!sameName && (
                <tr>
                  <td style={{ paddingLeft: 24, fontWeight: 'bold' }}>
                    {sec.section}
                  </td>
                  <td></td>
                </tr>
              )}

              {/* Accounts */}
              {/* Accounts */}
              {sec.rows.map((r, k) => {
                // ✅ Log each account row
                console.log('Account row:', r);

                return (
                  <tr key={k}>
                    <td style={{ paddingLeft: 36 }}>{r.label}</td>
                    <td style={styles.amount}>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/form-report/transactions-list?account_type=${r.accountType}&main_account_id=${r.mainAccountId}&account_id=${r.accountId}&from=${value.from}&to=${value.to}`}
                      >
                        {formatAmount(r.amount)}
                      </a>
                    </td>
                  </tr>
                );
              })}

              {/* Section Total */}
              <tr>
                <td
                  style={{
                    paddingLeft: sameName ? 12 : 24,
                    fontWeight: 'bold',
                  }}
                >
                  Total {sameName ? sub.subType : sec.section}
                </td>
                <td style={styles.totalAmount}>{formatAmount(sec.total)}</td>
              </tr>
            </React.Fragment>
          );
        })}

        {/* SubAccountType Total only if multiple sections */}
        {sub.sections.length > 1 && (
          <tr>
            <td style={{ paddingLeft: 12, fontWeight: 'bold' }}>
              Total {sub.subType}
            </td>
            <td style={styles.totalAmount}>{formatAmount(sub.total)}</td>
          </tr>
        )}
      </React.Fragment>
    ));

  /* ---------------- UI ---------------- */
  return (
    <>
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <button className="btn btn-success me-2" onClick={exportExcel}>
          Export Excel
        </button>
        <button className="btn btn-secondary me-2" onClick={exportCSV}>
          Export CSV
        </button>
        <button className="btn btn-danger" onClick={exportPDF}>
          Export PDF
        </button>
      </div>

      <br />

      <div style={styles.container} ref={reportRef}>
        <div style={styles.headerSection}>
          <img
            src={`${API_URL}/${companyBanner}`}
            alt="Banner"
            style={styles.logo}
          />
          <h2>{companyName}</h2>
          <h3 style={{ fontWeight: 'bold' }}>
            Balance Sheet as of <br />
            <small>{value.to}</small>
          </h3>
        </div>

        <table style={styles.table}>
          <tbody>
            {/* ASSETS */}
            <tr>
              <td style={styles.mainAccount}>ASSETS</td>
              <td></td>
            </tr>

            {renderAccountType(statement.assets)}

            <tr>
              <td style={styles.grandTotal}>TOTAL ASSETS</td>
              <td style={styles.grandTotalAmount}>
                {formatAmount(statement.totals.assets)}
              </td>
            </tr>

            {/*Liabilities Total */}

            <tr>
              <td style={styles.mainAccount}>LIABILITIES</td>
              <td></td>
            </tr>

            {renderAccountType(statement.liabilities)}

            <tr>
              <td style={styles.grandTotal}>TOTAL LIABILITIES</td>
              <td style={styles.grandTotalAmount}>
                {formatAmount(statement.totals.liabilities)}
              </td>
            </tr>
            {/*Equity Total */}

            <tr>
              <td style={styles.mainAccount}>EQUITY</td>
              <td></td>
            </tr>

            {renderAccountType(statement.equity)}

            <tr>
              <td style={styles.grandTotal}>TOTAL EQUITY</td>
              <td style={styles.grandTotalAmount}>
                {formatAmount(statement.totals.equity)}
              </td>
            </tr>
            {/*Final Total */}
            <tr>
              <td style={styles.grandTotal}>TOTAL LIABILITIES AND EQUITY</td>
              <td style={styles.grandTotalAmount}>
                {formatAmount(
                  statement.totals.liabilities + statement.totals.equity
                )}{' '}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <br />
      <br />
    </>
  );
};

/* ---------------- STYLES ---------------- */
const styles = {
  container: { width: '40%', margin: 'auto' },
  headerSection: { textAlign: 'center', marginBottom: 20 },
  logo: { width: '100%', height: 150, objectFit: 'cover' },
  table: { width: '100%' },
  mainAccount: { fontWeight: 'bold', paddingTop: 10 },
  section: { fontWeight: 'bold', paddingTop: 5 },
  amount: { textAlign: 'right' },
  totalLabel: { fontWeight: 'bold', paddingTop: 5 },
  totalAmount: { fontWeight: 'bold', textAlign: 'right' },
  grandTotal: {
    fontWeight: 'bold',
    borderTop: '2px solid black',
    paddingTop: 8,
  },
  grandTotalAmount: {
    fontWeight: 'bold',
    textAlign: 'right',
    borderTop: '2px solid black',
  },
  subAccountType: {
    fontWeight: 'bold',
    paddingTop: 8,
    textTransform: 'uppercase',
  },
};

export default BalanceSheet;
